import { assertEqualBy } from '@upright/common/utils/assertion-helpers';
import { safeAssert as assert } from '@upright/common/safe-assert';
import { groupBy, head, sum, sumBy, tail, values } from 'lodash';
import { CompanyProductSetProduct, ProductRatios, RevenueShareClass } from './types';
import { assertNoDuplicateProducts, mergeProducts } from './utils';
export type { VectorMatchResult } from './mapping-service-types';
export type { MappingServiceProductMatchResult } from './mapping-service-types';

/** Duplicate-safe wrapper for productSetRatios. See unit test and main funciton comments for detailed behaviour */
export function productSetToRatios(products: CompanyProductSetProduct[], debugId = ''): ProductRatios {
  const nonDuplicatedProductSet = toNonDuplicatedProductSet(products);
  assertNoDuplicateProducts(nonDuplicatedProductSet);
  return productSetToRatiosUnsafe(nonDuplicatedProductSet, debugId);
}

function toNonDuplicatedProductSet(products: CompanyProductSetProduct[]) {
  const productSetProductsByCode = groupBy(products, product => product.productCode);
  return values(productSetProductsByCode).map(mergeDuplicatedProducts);
}

function mergeDuplicatedProducts(products: CompanyProductSetProduct[]): CompanyProductSetProduct {
  assertEqualBy(products, ['productCode']);
  const firstProduct = head(products);
  const merged = tail(products).reduce(mergeProducts, firstProduct);
  assert(merged);
  return merged;
}

/**
 * WARNING: This is an unsafe function that returns wonky results if there are duplicated products. Use the above wrapper for duplicate-safety (TM 5.4.2022)
 *
 * Logic: The products with a revenueShare are first added up. The remaining
 * share is then split up between the rest of the products. Products without a
 * revenueShare or a revenueShareClass are assigned a medium revenue share class.
 * If the resulting sum of revenue shares don't add up to 1 (i.e. revenueShare
 * properties add up to >1, or they sum up to <1 and there are no other products)
 * we scale the resulting ratios so that they add up to one.
 */
export function productSetToRatiosUnsafe(products: CompanyProductSetProduct[], debugId = ''): ProductRatios {
  const totalShareWithRevenueShare = sumBy(products, p => p.revenueShare || 0);

  // Split the remaining unassigned share between the rest of the products.
  const unassignedShare = Math.max(1 - totalShareWithRevenueShare, 0);
  const productsWithoutRevenueShare = products.filter(p => p.revenueShare == null || p.revenueShare === 0);
  const revenueSharePerRatio = computeRevenueSharesPerRatio(productsWithoutRevenueShare, unassignedShare);

  // If we only have products that have a revenueShare but they don't add up to
  // 1, scale the ratios so that this is the case.
  const total =
    productsWithoutRevenueShare.length === 0 || totalShareWithRevenueShare > 1 ? totalShareWithRevenueShare : 1;

  const productRatios = products.reduce<{ [code: string]: number }>((result, product) => {
    result[product.productCode] =
      (product.revenueShare ||
        revenueSharePerRatio *
          revenueShareRatios[product.revenueShareClass != null ? product.revenueShareClass : fallbackShareClass]) /
      total;
    return result;
  }, {});

  const ratioValues = values(productRatios);
  const sumOfRatios = sum(ratioValues);
  if (ratioValues.length !== products.length) {
    console.warn(`Dublicate products ${products.map(p => p.productCode).join(', ')}, debugId: ${debugId}`);
  } else if (ratioValues.length > 0 && Math.abs(sumOfRatios - 1) > 0.01) {
    console.warn(`Invalid sum of product ratios (${sumOfRatios}), debugId: ${debugId}`);
  }

  return productRatios;
}

const revenueShareRatios = {
  [RevenueShareClass.MINOR]: 1,
  [RevenueShareClass.MEDIUM]: 3,
  [RevenueShareClass.MAJOR]: 5,
  // OA Added the next line to keep Typescript happy. RevenueShareClass.UNKNOWN is never saved,
  // just used as an elemenent for the product set product card dropdown UI.
  [RevenueShareClass.UNKNOWN]: 3,
};
const fallbackShareClass = RevenueShareClass.MEDIUM;

export function getRevenueShareRatio(revenueShareClass: RevenueShareClass | undefined): number {
  return revenueShareRatios[revenueShareClass != null ? revenueShareClass : fallbackShareClass];
}

export function computeRevenueSharesPerRatio(
  productsWithoutRevenueShare: Array<Pick<CompanyProductSetProduct, 'revenueShareClass'>>,
  unassignedShare: number,
) {
  const totalRevenueShareRatioShares = sumBy(
    productsWithoutRevenueShare,
    p => revenueShareRatios[p.revenueShareClass != null ? p.revenueShareClass : fallbackShareClass],
  );
  const revenueSharePerRatio = totalRevenueShareRatioShares > 0 ? unassignedShare / totalRevenueShareRatioShares : 0;
  return revenueSharePerRatio;
}
